<template>
    <!-- Switch Modem: move a licence to a free port on another server.
         Same picker as the quick add: UK / USA / Others, servers with free ports only,
         a random free port unless a specific one is chosen. -->
    <div class="modal fade" id="modemChangeModal" tabindex="-1" aria-hidden="true" ref="modalEl">
        <div class="modal-dialog modal-dialog-centered w-[640px] max-w-[96vw]">
            <div class="modal-content" v-if="props.license_uuid">
                <div class="modal-header">
                    <h1 class="modal-title fs-5">Move licence to another server</h1>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
                </div>
                <div class="modal-body">
                    <div v-if="props.license" class="px-2 pb-2 text-sm">
                        <b>{{ props.license.customer_email }}</b> · currently {{ props.license.modem_name }}
                        · ports {{ props.license.proxy_http_port }}/{{ props.license.proxy_sock_port }}
                        <span v-if="props.license.package_name"> · {{ props.license.package_name }}</span>
                    </div>
                    <div class="px-2 py-2 modal-item">
                        <div class="flex items-center justify-between">
                            <span>Server{{ loading ? ' (refreshing…)' : '' }}:</span>
                            
                        </div>
                        <v-autocomplete v-model="selectedServer" :items="visibleServers" item-title="label" item-value="modem_name"
                            return-object rounded="0" variant="outlined" density="compact" hide-details
                            placeholder="Type a server name" no-data-text="No server in this region has a free port"></v-autocomplete>
                    </div>
                    <div class="flex">
                        <div class="w-1/2 px-2 py-2 modal-item">
                            Port:
                            <v-select v-model="selectedPort" :items="portChoices" item-title="title" item-value="value"
                                rounded="0" variant="outlined" density="compact" hide-details :disabled="!selectedServer"></v-select>
                        </div>
                        <div class="w-1/2 px-2 py-2 modal-item">
                            Package:
                            <v-select v-model="selectedPackage" :items="packageList.map(p => p.name)" rounded="0" variant="outlined"
                                density="compact" hide-details :disabled="!selectedServer" no-data-text="No packages on this server"></v-select>
                        </div>
                    </div>
                    <div v-if="selectedServer" class="px-2 pt-1 text-xs text-gray-500">
                        {{ selectedServer.server_type }} · {{ selectedServer.city }}, {{ selectedServer.isp }} ·
                        {{ selectedPort === RANDOM ? 'one of ' + selectedServer.count + ' free ports, chosen at random' : 'port ' + selectedPort }}.
                        Credentials and whitelist move with the licence; the old port goes back to stock. Expiry is unchanged.
                    </div>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
                    <button type="button" :disabled="busy || !selectedServer || !selectedPackage" class="btn btn-primary"
                        @click="handleUpdate">{{ busy ? 'Moving…' : 'Move licence' }}</button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, defineProps, defineEmits } from "vue";
import { useAdminLicenseStore } from "@/stores"

const emit = defineEmits(['updated'])
const store = useAdminLicenseStore()
const props = defineProps({
    selectedServerName: { type: String, default: '' },   // kept for compatibility, not used
    license_uuid: { type: String, default: '' },
    license: { type: Object, default: null },            // the licence row (for the summary line + package period)
});

const RANDOM = '__random__'
const modalEl = ref(null)
const loading = ref(false)
const busy = ref(false)
const ports = ref([])            // every free port across servers, from the server
const selectedServer = ref(null)
const selectedPort = ref(RANDOM)
const packageList = ref([])
const selectedPackage = ref(null)

// region filter, same grouping as the quick add
const regions = [{ key: 'uk', label: 'UK' }, { key: 'usa', label: 'USA' }, { key: 'rest', label: 'Others' }]
const region = ref('all')   // one country: every server
const regionOf = (country) => {
    const c = String(country || '').toLowerCase()
    if (c.includes('united kingdom') || c === 'uk' || c.includes('great britain') || c.includes('england')) return 'uk'
    if (c.includes('united states') || c === 'usa' || c === 'us' || c.includes('america')) return 'usa'
    return 'rest'
}
const servers = computed(() => {
    const byServer = new Map()
    for (const p of ports.value) {
        if (!byServer.has(p.modem_name)) byServer.set(p.modem_name, { modem_name: p.modem_name, server_type: p.server_type, city: p.city, isp: p.isp, country: p.country, free: [] })
        byServer.get(p.modem_name).free.push(p.ports)
    }
    return [...byServer.values()]
        .filter(s => !props.license || s.modem_name !== props.license.modem_name)   // not the server it is already on
        .map(s => ({ ...s, count: s.free.length, label: s.modem_name + ' - (' + s.free.length + ' available)' }))
        .sort((a, b) => a.modem_name.localeCompare(b.modem_name))
})
const visibleServers = computed(() => servers.value.filter(s => region.value === 'all' || regionOf(s.country) === region.value))
const countFor = (key) => servers.value.filter(s => regionOf(s.country) === key).length
const portChoices = computed(() => [{ title: 'Random free port', value: RANDOM }, ...((selectedServer.value?.free || []).map(p => ({ title: p, value: p })))])

// pick the region the licence is currently in, so the list opens on the likely target
const openOn = () => {
    if (props.license?.country) region.value = regionOf(props.license.country)
    else if (ports.value.length && props.license?.modem_name) {
        const cur = ports.value.find(p => p.modem_name === props.license.modem_name)
        if (cur) region.value = regionOf(cur.country)
    }
}

async function refresh() {
    selectedServer.value = null; selectedPort.value = RANDOM; packageList.value = []; selectedPackage.value = null
    loading.value = true
    ports.value = (await store.getAvailablePorts()) || []
    loading.value = false
    openOn()
}
onMounted(() => modalEl.value && modalEl.value.addEventListener('shown.bs.modal', refresh))
onBeforeUnmount(() => modalEl.value && modalEl.value.removeEventListener('shown.bs.modal', refresh))

// packages of the chosen server; default to the same period as the licence's current package
watch(selectedServer, async (s) => {
    selectedPort.value = RANDOM
    packageList.value = []; selectedPackage.value = null
    if (!s) return
    const { pkgList } = await store.getProxiesAndPackages(s.modem_name)
    packageList.value = Array.isArray(pkgList) ? pkgList : []
    const unit = String(props.license?.package_timeunit || props.license?.time_unit || '').toUpperCase()
    const same = packageList.value.find(p => String(p.time_unit).toUpperCase() === unit)
    selectedPackage.value = (same || packageList.value[0])?.name || null
})

const handleUpdate = async () => {
    const s = selectedServer.value
    if (!s || !selectedPackage.value) return
    const port = selectedPort.value === RANDOM ? s.free[Math.floor(Math.random() * s.free.length)] : selectedPort.value
    busy.value = true
    await store.changeModem({ license_uuid: props.license_uuid, modemName: s.modem_name, proxyName: port, packageName: selectedPackage.value })
    busy.value = false
    emit('updated')
}
</script>
